import type { WeatherIconKind } from '../state/welcome-weather.js';

/**
 * Animated weather icon for the welcome splash. Pure SVG + CSS (`.wx-*` in styles.css), so it
 * needs no assets and the global reduced-motion rule freezes every animation.
 */
export function WeatherIcon({ kind }: { kind: WeatherIconKind }) {
  return (
    <svg className={`weather-icon wx-${kind}`} viewBox="0 0 64 64" aria-hidden="true">
      {kind === 'sun' && <Sun cx={32} cy={32} r={11} />}
      {kind === 'moon' && <Moon cx={32} cy={32} />}
      {kind === 'partly-sun' && (
        <>
          <Sun cx={24} cy={24} r={9} />
          <Cloud y={8} />
        </>
      )}
      {kind === 'partly-moon' && (
        <>
          <Moon cx={24} cy={24} small />
          <Cloud y={8} />
        </>
      )}
      {kind === 'cloud' && (
        <>
          <Cloud y={-8} back />
          <Cloud y={2} />
        </>
      )}
      {kind === 'fog' && (
        <>
          <Cloud y={-8} />
          <g className="wx-fog-lines">
            <rect x="10" y="44" width="40" height="3" rx="1.5" />
            <rect x="16" y="51" width="38" height="3" rx="1.5" />
            <rect x="8" y="58" width="34" height="3" rx="1.5" />
          </g>
        </>
      )}
      {(kind === 'rain' || kind === 'thunder' || kind === 'sleet') && (
        <>
          <Drops kind={kind === 'sleet' ? 'sleet' : 'rain'} />
          {kind === 'thunder' && (
            <polygon className="wx-bolt" points="34,36 26,50 32,50 28,62 40,46 33,46 37,36" />
          )}
          <Cloud y={-8} />
        </>
      )}
      {kind === 'snow' && (
        <>
          <Drops kind="snow" />
          <Cloud y={-8} />
        </>
      )}
    </svg>
  );
}

function Sun({ cx, cy, r }: { cx: number; cy: number; r: number }) {
  const rays = Array.from({ length: 8 }, (_, i) => i * 45);
  return (
    <g className="wx-sun">
      <g className="wx-rays">
        {rays.map((angle) => (
          <rect
            key={angle}
            x={cx - 1.5}
            y={cy - r - 9}
            width="3"
            height="6"
            rx="1.5"
            transform={`rotate(${angle} ${cx} ${cy})`}
          />
        ))}
      </g>
      <circle className="wx-sun-disc" cx={cx} cy={cy} r={r} />
    </g>
  );
}

function Moon({ cx, cy, small = false }: { cx: number; cy: number; small?: boolean }) {
  const r = small ? 9 : 13;
  const x = cx + r * 0.35; // the crescent sits left of its centre line
  return (
    <g className="wx-moon">
      {!small && (
        <g className="wx-stars">
          <circle cx={cx + 18} cy={cy - 16} r="1.6" />
          <circle cx={cx - 20} cy={cy - 10} r="1.2" />
          <circle cx={cx + 14} cy={cy + 18} r="1.4" />
        </g>
      )}
      {/* Crescent: the left half of the disc minus a narrower half-ellipse. */}
      <path
        className="wx-moon-disc"
        d={`M ${x} ${cy - r} A ${r} ${r} 0 0 0 ${x} ${cy + r} A ${r * 0.45} ${r} 0 0 1 ${x} ${cy - r} Z`}
      />
    </g>
  );
}

/** A puffy cloud centred horizontally; `y` shifts it from the middle of the icon. */
function Cloud({ y, back = false }: { y: number; back?: boolean }) {
  return (
    <g className={back ? 'wx-cloud wx-cloud-back' : 'wx-cloud'} transform={`translate(0 ${y})`}>
      <circle cx="24" cy="34" r="9" />
      <circle cx="35" cy="29" r="12" />
      <circle cx="45" cy="36" r="7" />
      <rect x="15" y="34" width="37" height="9" rx="4.5" />
    </g>
  );
}

const DROP_COLUMNS = [20, 28, 36, 44];

function Drops({ kind }: { kind: 'rain' | 'sleet' | 'snow' }) {
  return (
    <g className={`wx-drops wx-drops-${kind}`}>
      {DROP_COLUMNS.map((x, i) => {
        const flake = kind === 'snow' || (kind === 'sleet' && i % 2 === 1);
        return flake ? (
          <circle key={x} className="wx-flake" cx={x} cy="44" r="2.2" />
        ) : (
          <rect key={x} className="wx-drop" x={x - 1} y="40" width="2" height="7" rx="1" />
        );
      })}
    </g>
  );
}
